import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCalendarCheck,
  faChartLine,
  faCheck,
  faClock,
  faHeadset,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import logoImage from '../../img/logo.png';
import { createDemoRequest } from '../../services/publicSalesApi';
import { listPublicPlans } from '../../services/salesContractApi';
import '../../styles/DemonstrationPage.css';

const PROFESSIONAL_OPTIONS = [
  { value: '', label: 'Selecione uma opção' },
  { value: '1', label: 'Somente eu' },
  { value: '2-3', label: '2 a 3 profissionais' },
  { value: '4-10', label: '4 a 10 profissionais' },
  { value: '11+', label: 'Mais de 10 profissionais' },
];

const NEED_OPTIONS = [
  { value: '', label: 'Selecione a principal necessidade' },
  { value: 'whatsapp_campanhas', label: 'Enviar campanhas pelo WhatsApp Oficial' },
  { value: 'instagram', label: 'Centralizar comentários e Direct do Instagram' },
  { value: 'automacao', label: 'Automatizar atendimento e qualificação de leads' },
  { value: 'crm_pipeline', label: 'Organizar leads no CRM e Pipeline de Vendas' },
  { value: 'agenda', label: 'Organizar agenda e atendimentos' },
  { value: 'financeiro', label: 'Melhorar o controle financeiro' },
  { value: 'estoque', label: 'Controlar materiais e estoque' },
  { value: 'pacientes', label: 'Centralizar pacientes e prontuários' },
  { value: 'gestao_completa', label: 'Integrar toda a gestão da clínica' },
  { value: 'outro', label: 'Outra necessidade' },
];

const DEFAULT_PLAN_ID = 'fluxo-clinica-conecta';

const SALES_PLANS = Object.freeze({
  'fluxo-crm': {
    id: 'fluxo-crm',
    name: 'Fluxo CRM',
    optionLabel: 'Fluxo CRM — qualquer segmento',
  },
  'fluxo-clinica': {
    id: 'fluxo-clinica',
    name: 'Fluxo Clínica',
    optionLabel: 'Fluxo Clínica — gestão sem CRM',
  },
  'fluxo-clinica-conecta': {
    id: 'fluxo-clinica-conecta',
    name: 'Fluxo Clínica Conecta',
    optionLabel: 'Fluxo Clínica Conecta — com CRM',
  },
  'fluxo-clinica-escala': {
    id: 'fluxo-clinica-escala',
    name: 'Fluxo Clínica Escala',
    optionLabel: 'Fluxo Clínica Escala — CRM e Campanhas',
  },
});

const PLAN_OPTIONS = Object.values(SALES_PLANS).map((plan) => ({
  value: plan.id,
  label: plan.optionLabel,
}));

const ATTRIBUTION_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
];

const formatPhone = (rawValue) => {
  const digits = rawValue.replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 2) return digits ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const DemonstrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialInterest = 'demonstracao';
  const requestedPlanId = (searchParams.get('plano') || '').trim().toLowerCase();
  const initialPlanId = Object.prototype.hasOwnProperty.call(SALES_PLANS, requestedPlanId)
    ? requestedPlanId
    : DEFAULT_PLAN_ID;

  const attribution = useMemo(() => {
    const values = {};
    ATTRIBUTION_FIELDS.forEach((field) => {
      const value = searchParams.get(field);
      if (value) values[field] = value;
    });
    return values;
  }, [searchParams]);

  const [formData, setFormData] = useState({
    nome: '',
    nome_clinica: '',
    whatsapp: '',
    email: '',
    cidade: '',
    estado: '',
    especialidade: '',
    quantidade_profissionais: '',
    principal_necessidade: '',
    plano_interesse: initialPlanId,
    interesse: initialInterest,
    consentimento: false,
    website: '',
  });
  const [salesPlans, setSalesPlans] = useState([]);
  useEffect(() => {
    listPublicPlans().then(({ data }) => setSalesPlans(data)).catch(() => setSalesPlans([]));
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const localPlan = SALES_PLANS[formData.plano_interesse] || SALES_PLANS[DEFAULT_PLAN_ID];
  const apiPlan = salesPlans.find((plan) => plan.slug === localPlan.id);
  const displayedPlan = {
    ...localPlan,
    ...apiPlan,
    id: apiPlan?.slug || localPlan.id,
    monthlyPrice: apiPlan ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(apiPlan.price) : 'Consulte',
    implantationPrice: apiPlan ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(apiPlan.implementation_price || 0) : 'Consulte',
  };

  const isCrmPlan = displayedPlan.id === 'fluxo-crm';

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    let nextValue = type === 'checkbox' ? checked : value;

    if (name === 'whatsapp') nextValue = formatPhone(value);
    if (name === 'estado') nextValue = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);

    setFormData((current) => ({ ...current, [name]: nextValue }));
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (formData.website) {
      navigate('/demonstracao/obrigado', { replace: true });
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      await createDemoRequest({
        nome: formData.nome.trim(),
        nome_clinica: formData.nome_clinica.trim(),
        whatsapp: formData.whatsapp.replace(/\D/g, ''),
        email: formData.email.trim().toLowerCase(),
        cidade: formData.cidade.trim(),
        estado: formData.estado,
        especialidade: formData.especialidade.trim(),
        quantidade_profissionais: formData.quantidade_profissionais,
        principal_necessidade: formData.principal_necessidade,
        plano_interesse: displayedPlan.id,
        interesse: formData.interesse,
        consentimento: formData.consentimento,
        consentimento_em: new Date().toISOString(),
        origem: 'landing_page',
        pagina_origem: window.location.href,
        referencia: document.referrer || null,
        website: formData.website,
        ...attribution,
      });

      navigate('/demonstracao/obrigado', {
        replace: true,
        state: { firstName: formData.nome.trim().split(' ')[0] },
      });
    } catch (error) {
      setSubmitError(
        error.response?.data?.message
        || 'Não foi possível enviar sua solicitação agora. Seus dados continuam preenchidos; tente novamente em alguns instantes.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="demonstration-page">
      <header className="demonstration-page__header">
        <Link to="/inicio" aria-label="Voltar para a página inicial">
          <img src={logoImage} alt="Fluxo Clínica" className="demonstration-page__logo" />
        </Link>
        <Link to="/inicio" className="demonstration-page__back-link">
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar para o início
        </Link>
      </header>

      <main className="demonstration-page__main">
        <section className="demonstration-page__intro" aria-labelledby="demo-page-title">
          <span className="demonstration-page__eyebrow">{isCrmPlan ? 'Demonstração do Fluxo CRM' : 'Demonstração personalizada'}</span>
          <h1 id="demo-page-title">{isCrmPlan ? 'Transforme conversas em vendas com menos trabalho manual' : 'Veja como organizar sua clínica em um só lugar'}</h1>
          <p className="demonstration-page__lead">
            {isCrmPlan
              ? 'Veja como centralizar WhatsApp Oficial, Instagram, campanhas, automação e Pipeline de Vendas em uma única operação.'
              : 'Conte um pouco sobre sua operação. Vamos preparar uma conversa focada na sua rotina, sem criar conta ou iniciar cobrança neste momento.'}
          </p>

          <div className="demonstration-page__benefits">
            <div className="demonstration-page__benefit">
              <span><FontAwesomeIcon icon={faCalendarCheck} /></span>
              <div>
                <strong>Demonstração direcionada</strong>
                <p>{isCrmPlan ? 'CRM, Pipeline, Instagram, WhatsApp Oficial e Campanhas aplicados ao seu segmento.' : 'Agenda, pacientes, financeiro, estoque e marketing conforme sua necessidade.'}</p>
              </div>
            </div>
            <div className="demonstration-page__benefit">
              <span><FontAwesomeIcon icon={faClock} /></span>
              <div>
                <strong>Conversa objetiva</strong>
                <p>Entendemos seu cenário e mostramos como reduzir tarefas repetitivas e acelerar o atendimento.</p>
              </div>
            </div>
            <div className="demonstration-page__benefit">
              <span><FontAwesomeIcon icon={faHeadset} /></span>
              <div>
                <strong>Implantação acompanhada</strong>
                <p>Na contratação, configuramos canais, regras e fluxos para sua empresa iniciar com segurança.</p>
              </div>
            </div>
          </div>

          <div className="demonstration-page__offer">
            <div>
              <small>{displayedPlan.name} · mensalidade</small>
              <strong>{displayedPlan.monthlyPrice} <em>/mês</em></strong>
            </div>
            <div>
              <small>Implantação</small>
              <strong>{displayedPlan.implantationPrice} <em>uma vez</em></strong>
            </div>
            <p>
              <FontAwesomeIcon icon={faCheck} />
              A mensalidade e a implantação seguem as condições oficiais do plano selecionado.
            </p>
          </div>
        </section>

        <section className="demonstration-page__form-card" aria-labelledby="demo-form-title">
          <div className="demonstration-page__form-heading">
            <span><FontAwesomeIcon icon={faChartLine} /></span>
            <div>
              <h2 id="demo-form-title">Agende uma demonstração</h2>
              <p>Preencha os dados e entraremos em contato pelo WhatsApp.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
<div className="demonstration-page__field-grid">
              <label className="demonstration-page__field">
                <span>Seu nome *</span>
                <input name="nome" type="text" value={formData.nome} onChange={handleChange} autoComplete="name" maxLength={120} required />
              </label>
              <label className="demonstration-page__field">
                <span>Nome da empresa ou clínica *</span>
                <input name="nome_clinica" type="text" value={formData.nome_clinica} onChange={handleChange} autoComplete="organization" maxLength={150} required />
              </label>
              <label className="demonstration-page__field">
                <span>WhatsApp *</span>
                <input name="whatsapp" type="tel" value={formData.whatsapp} onChange={handleChange} autoComplete="tel" inputMode="tel" placeholder="(00) 00000-0000" minLength={15} required />
              </label>
              <label className="demonstration-page__field">
                <span>E-mail *</span>
                <input name="email" type="email" value={formData.email} onChange={handleChange} autoComplete="email" maxLength={150} required />
              </label>
              <label className="demonstration-page__field">
                <span>Cidade *</span>
                <input name="cidade" type="text" value={formData.cidade} onChange={handleChange} autoComplete="address-level2" maxLength={100} required />
              </label>
              <label className="demonstration-page__field demonstration-page__field--state">
                <span>UF *</span>
                <input name="estado" type="text" value={formData.estado} onChange={handleChange} autoComplete="address-level1" maxLength={2} minLength={2} placeholder="SP" required />
              </label>
              <label className="demonstration-page__field demonstration-page__field--wide">
                <span>Segmento ou área de atuação *</span>
                <input name="especialidade" type="text" value={formData.especialidade} onChange={handleChange} maxLength={120} placeholder="Ex.: educação, imobiliária, agência, estética ou odontologia" required />
              </label>
              <label className="demonstration-page__field demonstration-page__field--wide">
                <span>Plano de maior interesse</span>
                <select name="plano_interesse" value={formData.plano_interesse} onChange={handleChange}>
                  {PLAN_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="demonstration-page__field demonstration-page__field--wide">
                <span>Quantidade de profissionais *</span>
                <select name="quantidade_profissionais" value={formData.quantidade_profissionais} onChange={handleChange} required>
                  {PROFESSIONAL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="demonstration-page__field demonstration-page__field--wide">
                <span>Principal necessidade *</span>
                <select name="principal_necessidade" value={formData.principal_necessidade} onChange={handleChange} required>
                  {NEED_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="demonstration-page__honeypot" aria-hidden="true">
              Website
              <input name="website" type="text" value={formData.website} onChange={handleChange} tabIndex="-1" autoComplete="off" />
            </label>

            <label className="demonstration-page__consent">
              <input name="consentimento" type="checkbox" checked={formData.consentimento} onChange={handleChange} required />
              <span>
                Autorizo o contato do Fluxo Clínica sobre esta solicitaçao. Meus dados
                não serão usados para criar uma conta automaticamente.
              </span>
            </label>

            {submitError && (
              <div className="demonstration-page__error" role="alert" aria-live="assertive">
                {submitError}
              </div>
            )}

            <button type="submit" className="demonstration-page__submit" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando solicitação...' : 'Solicitar contato'}
              {!isSubmitting && <FontAwesomeIcon icon={faArrowRight} />}
            </button>

            <p className="demonstration-page__security-note">
              <FontAwesomeIcon icon={faShieldHalved} />
              O envio não inicia cobrança. A demonstração é gratuita e sem compromisso.
            </p>
          </form>
        </section>
      </main>
    </div>
  );
};

export default DemonstrationPage;
